import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DatePickerComponent, Inject, MaskedDateTime } from '@syncfusion/ej2-react-calendars';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { MaskedTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import moment from 'moment';
import { Row, Col } from 'react-bootstrap';
import { Validate } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { formatUTCDate } from 'common/widgets';
import ControlledProviderDataTextBox from 'components/Common/CustomTextBoxWrapper/ControlledProviderDataTextBox';
import styles from 'components/ProviderData/IpaAdministration/organisms/IpaAdministrationWizard/IpaAdministrationWizardDetails.module.css';

type IpaAdministrationWizardDetailsProps = {
    asyncDebounce: any;
    checkIpaCode: any;
    register: any;
    setValue: any;
    getValues: any;
    errors: Record<string, any>;
    risks: Record<string, any>;
    types: Record<string, any>;
    termed: string | undefined;
    mso: Record<string, any>;
};

const IpaAdministrationWizardDetails = (props: IpaAdministrationWizardDetailsProps) => {
    const {
        control
    } = useForm<any>();

    return (
        <div className='infoStepsMainWrap'>
            <div className='accordion-content'>
                <Row>
                    <Col
                        md={6}
                        className={`${styles['info-steps-wrapper']} px-4`}
                    >
                        <div className='infoStepsWrap'>
                            <p className='section-container-header mx-0'>Main Info</p>
                        </div>
                        <Row>
                            <Col md={6}>
                                <div className={`${styles['date-picker']} datepicker e-input-group-margin`}>
                                    <label className='e-label-select'>Effective Date *</label>
                                    <DatePickerComponent
                                        format='MM/dd/yyyy'
                                        enableMask={true}
                                        id='calendar_start'
                                        {...props?.register('effDate', {
                                            required: 'Effective Date is required',
                                        })}
                                        change={({ value }) => props?.setValue('effDate', value)}
                                        min={undefined}
                                        max={new Date(9999, 11, 31)}
                                        strictMode
                                    >
                                        <Inject services={[MaskedDateTime]} />
                                    </DatePickerComponent>
                                    {props?.errors?.effDate && (
                                        <span className='error-form'>{props.errors.effDate.message}</span>
                                    )}
                                </div>
                            </Col>

                            <Col md={6}>
                                <div className={`${styles['date-picker']} datepicker e-input-group-margin`}>
                                    <label className='e-label-select'>Term Date *</label>
                                    <DatePickerComponent
                                        format='MM/dd/yyyy'
                                        enableMask={true}
                                        id='calendar_end'
                                        {...props?.register('endDate', {
                                            required: 'Term Date is required',
                                            validate: (value) => {
                                                const effDate = moment(new Date(props?.getValues('effDate')));
                                                const endDate = moment(new Date(props?.getValues('endDate')));
                                                if (value && effDate && endDate.isBefore(effDate)) {
                                                    return `End date must be Greater than Effective Date`;
                                                }
                                            },
                                        })}
                                        {...props?.register('endDate', {
                                            required: 'Term Date is required',
                                        })}
                                        change={({ value }) => props?.setValue('endDate', value)}
                                        min={undefined}
                                        max={new Date(9999, 11, 31)}
                                        strictMode
                                    >
                                        <Inject services={[MaskedDateTime]} />
                                    </DatePickerComponent>
                                    {props?.errors?.endDate && (
                                        <span className='error-form'>{props?.errors.endDate.message}</span>
                                    )}
                                </div>
                            </Col>
                        </Row>
                        <Col md={12}>
                            <div className={`e-float-input e-input-group e-input-group-margin`}>
                                <input
                                    type='text'
                                    {...props?.register('ipaCode', {
                                        required: 'IPA Code is required',
                                        maxLength: {
                                            value: 12,
                                            message: 'IPA Code must be maximum 12 characters length',
                                        },
                                        validate: {
                                            checkUrl: props?.asyncDebounce(async (value) => {
                                                const effDate = formatUTCDate(props?.getValues('effDate'));
                                                return (
                                                    (await props?.checkIpaCode(value, effDate)) ||
                                                    'That IPA Code already exists'
                                                );
                                            }, 1000) as Validate<string>,
                                        },
                                    })}
                                />
                                <span className='e-float-line'></span>
                                <label className='e-float-text'>IPA Code *</label>
                            </div>
                            {props?.errors?.ipaCode && <p className='error-form'>{props.errors.ipaCode.message}</p>}
                        </Col>
                        <Col md={12}>
                            <div>
                                <label className='col-md-12 mb-1'>IPA Description</label>
                                <ControlledProviderDataTextBox
                                    name='ipaDesc'
                                    control={control}
                                    boxAttrs={{
                                        htmlAttributes: {
                                            maxlength: '100',
                                        },
                                    }}
                                    onChangeHandler={(value) => {
                                        props?.setValue('ipaDesc', value)
                                    }}
                                />
                            </div>
                        </Col>
                        <Row>
                            <Col md={6}>
                                <div className='e-input-group-margin'>
                                    <div className='control-label label-mask'>IPA Phone 1</div>
                                    <MaskedTextBoxComponent
                                        mask='(000) 000-0000'
                                        {...props?.register('ipaPhone', {
                                            maxLength: 10,
                                            minLength: 10,
                                        })}
                                    />
                                </div>
                                {props?.errors?.ipaPhone && (
                                    <p className='error-form'>Incorrect format phone number (123) 123-1234</p>
                                )}
                            </Col>
                            <Col md={6}>
                                <div className='e-input-group-margin'>
                                    <div className='control-label label-mask'>IPA Phone 2</div>
                                    <MaskedTextBoxComponent
                                        mask='(000) 000-0000'
                                        {...props?.register('ipaPhone2', {
                                            maxLength: 10,
                                            minLength: 10,
                                        })}
                                    />
                                </div>
                                {props?.errors?.ipaPhone2 && (
                                    <p className='error-form'>Incorrect format phone number (123)123-1234</p>
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <div className='e-input-group-margin'>
                                    <label className='e-label-select'>Contract Type *</label>
                                    <DropDownListComponent
                                        id='risk'
                                        dataSource={props?.risks}
                                        value={props?.getValues().risk}
                                        placeholder='Select a Contract Type'
                                        fields={{ text: 'text', value: 'id' }}
                                        change={({ itemData }) => props?.setValue('risk', itemData.id)}
                                        popupHeight='220px'
                                        {...props?.register('risk', { required: 'Contract Type is required' })}
                                    />
                                </div>
                                {props?.errors?.risk && <span className='error-form'>{props.errors.risk.message}</span>}
                            </Col>
                            <Col md={6}>
                                <div className='e-input-group-margin'>
                                    <label className='e-label-select'>Type *</label>
                                    <DropDownListComponent
                                        id='type'
                                        dataSource={props?.types}
                                        placeholder='Select a type'
                                        {...props?.register('type', { required: 'Type is required' })}
                                        change={({ value }) => {
                                            props?.setValue('type', value);
                                        }}
                                        popupHeight='220px'
                                    />
                                </div>
                                {props?.errors?.type && <p className='error-form'>{props.errors.type.message}</p>}
                            </Col>
                        </Row>
                        <Col md={12}>
                            <div style={{ display: 'flex' }}>
                                <div
                                    className={`e-float-input e-input-group e-input-group-margin`}
                                    style={{ marginRight: '8px' }}
                                >
                                    <input
                                        type='text'
                                        {...props?.register('webSite', {
                                            maxLength: {
                                                value: 200,
                                                message: 'Website must be maximum 200 characters length.',
                                            },
                                        })}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>Website</label>
                                </div>
                                <ButtonComponent
                                    type='button'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (props?.getValues('webSite')) {
                                            const website = '//' + props?.getValues('webSite');
                                            window.open(website, '_blank');
                                        }
                                    }}
                                    className={`${styles['view-button']}`}
                                    cssClass='e-primary btn-primary'
                                >
                                    View
                                </ButtonComponent>
                            </div>
                            {props?.errors?.webSite && <p className='error-form'>{props.errors.webSite.message}</p>}
                        </Col>
                        <div className='e-float-input e-input-group'>
                            <textarea
                                rows={4}
                                {...props?.register('notes', {
                                    maxLength: {
                                        value: 50,
                                        message: 'Notes must be maximum 50 characters length',
                                    },
                                })}
                            />
                            <span className='e-float-line'></span>
                            <label className='e-float-text'>Notes</label>
                        </div>
                        {props?.errors?.notes && <p className='error-form'>{props.errors.notes.message}</p>}
                    </Col>
                    <Col
                        md={6}
                        className='px-4'
                    >
                        <div className='infoStepsWrap'>
                            <p className='section-container-header mx-0'>Vendor Details</p>
                        </div>
                        <Row>
                            <Col md={12}>
                                <div className={`e-float-input e-input-group e-input-group-margin`}>
                                    <input
                                        type='text'
                                        {...props?.register('vendorName', {
                                            maxLength: {
                                                value: 100,
                                                message: 'Vendor Name must be maximum 100 characters length',
                                            },
                                        })}
                                        className={`${styles['vendor-input']}`}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>Vendor Name</label>
                                </div>
                                {props?.errors?.vendorName && <p className='error-form'>{props.errors.vendorName.message}</p>}
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <div className={`e-float-input e-input-group e-input-group-margin`}>
                                    <input
                                        type='text'
                                        {...props?.register('taxId', {
                                            maxLength: {
                                                value: 60,
                                                message: 'Tax ID must be maximum 60 characters length',
                                            },
                                        })}
                                        className={`${styles['vendor-input']}`}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>Tax ID</label>
                                </div>
                                {props?.errors?.taxId && <p className='error-form'>{props.errors.taxId.message}</p>}
                            </Col>
                            <Col md={6}>
                                <div className={`e-float-input e-input-group e-input-group-margin`}>
                                    <input
                                        type='text'
                                        {...props?.register('npi', {
                                            maxLength: {
                                                value: 60,
                                                message: 'NPI must be maximum 60 characters length',
                                            },
                                        })}
                                        className={`${styles['vendor-input']}`}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>NPI</label>
                                </div>
                                {props?.errors?.npi && <p className='error-form'>{props.errors.npi.message}</p>}
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <div className={`e-float-input e-input-group e-input-group-margin`}>
                                    <input
                                        type='text'
                                        {...props?.register('addr', {
                                            maxLength: {
                                                value: 80,
                                                message: 'Address must be maximum 80 characters length',
                                            },
                                        })}
                                        className={`${styles['vendor-input']}`}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>Street Address</label>
                                </div>
                                {props?.errors?.addr && <p className='error-form'>{props.errors.addr.message}</p>}
                            </Col>
                            <Col md={6}>
                                <div className={`e-float-input e-input-group e-input-group-margin`}>
                                    <input
                                        type='text'
                                        {...props?.register('addr2', {
                                            maxLength: {
                                                value: 80,
                                                message: 'This field must be maximum 80 characters length',
                                            },
                                        })}
                                        className={`${styles['vendor-input']}`}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>Suite/Bldg/No</label>
                                </div>
                                {props?.errors?.addr2 && <p className='error-form'>{props.errors.addr2.message}</p>}
                            </Col>
                        </Row>
                        <Row className='e-input-group-margin'>
                            <Col md={4}>
                                <div className={`e-float-input e-input-group e-input-group-margin`}>
                                    <input
                                        type='text'
                                        {...props?.register('city', {
                                            maxLength: {
                                                value: 80,
                                                message: 'City must be maximum 80 characters length',
                                            },
                                        })}
                                        className={`${styles['vendor-input']}`}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>City</label>
                                </div>
                                {props?.errors?.city && <p className='error-form'>{props.errors.city.message}</p>}
                            </Col>
                            <Col md={4}>
                                <div className={`e-float-input e-input-group e-input-group-margin`}>
                                    <input
                                        type='text'
                                        {...props?.register('state', {
                                            maxLength: {
                                                value: 2,
                                                message: 'State must be 2 characters length',
                                            },
                                            minLength: {
                                                value: 2,
                                                message: 'State must be 2 characters length',
                                            },
                                        })}
                                        className={`${styles['vendor-input']}`}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>State</label>
                                </div>
                                {props?.errors?.state && <p className='error-form'>{props.errors.state.message}</p>}
                            </Col>
                            <Col md={4}>
                                <div className={`e-float-input e-input-group e-input-group-margin`}>
                                    <input
                                        type='text'
                                        {...props?.register('zip', {
                                            maxLength: {
                                                value: 80,
                                                message: 'Zip Code must be maximum 80 characters length',
                                            },
                                        })}
                                        className={`${styles['vendor-input']}`}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>Zip Code</label>
                                </div>
                                {props?.errors?.zip && <p className='error-form'>{props.errors.zip.message}</p>}
                            </Col>
                        </Row>
                        <Row className='e-input-group-margin'>
                            <Col md={4}>
                                <div className={`e-float-input e-input-group e-input-group-margin`}>
                                    <input
                                        type='text'
                                        {...props?.register('vendorFaxNumber', {
                                                pattern: {
                                                value: /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                                                message: 'Fax is not valid',
                                            },
                                        })}
                                        className={`${styles['vendor-input']}`}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>Fax</label>
                                </div>
                                {props?.errors?.vendorFaxNumber && <p className='error-form'>{props.errors.vendorFaxNumber.message}</p>}
                            </Col>
                            <Col md={4}>
                                <div className='e-input-group-margin'>
                                    <div className='control-label label-mask'>Phone</div>
                                    <MaskedTextBoxComponent
                                        mask='(000) 000-0000'
                                        {...props?.register('vendorPhoneNumber', {
                                            maxLength: 10,
                                            minLength: 10,
                                        })}
                                    />
                                </div>
                                {props?.errors?.vendorPhoneNumber && <p className='error-form'>Incorrect format phone number (123)123-1234</p>}
                            </Col>
                            <Col md={4}>
                                <div className={`e-float-input e-input-group e-input-group-margin`}>
                                    <input
                                        type='text'
                                        {...props?.register('emailAddress', {
                                            required: 'email is required',
                                            pattern: {
                                                value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                                message: 'Please enter a valid email',
                                            },
                                        })}
                                        className={`${styles['vendor-input']}`}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>Email</label>
                                </div>
                                {props?.errors?.emailAddress && <p className='error-form'>{props.errors.emailAddress.message}</p>}
                            </Col>
                        </Row>
                        <Row>
                            <div className='infoStepsWrap'>
                                <p className='section-container-header mx-0'>Contract & MCO Details</p>
                            </div>
                            <Col md={12}>
                                <div className={`e-float-input e-input-group e-input-group-margin disable-field`}>
                                    <input
                                        type='text'
                                        {...props?.register('contractName')}
                                        value={props?.getValues().contractName}
                                        className={`${styles['vendor-input']}`}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>Contract Name</label>
                                </div>
                                {props?.termed === 'Y' && <span className='error-form'>The contract is termed</span>}
                            </Col>
                        </Row>
                        <Col md={12}>
                            <div className={`e-float-input e-input-group e-input-group-margin disable-field`}>
                                <input
                                    type='text'
                                    {...props?.register('msoName')}
                                    value={props?.mso?.name}
                                />
                                <label className='e-float-text'>MSO Name</label>
                            </div>
                        </Col>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default IpaAdministrationWizardDetails;
